import React, { useEffect, useRef, useState } from "react";
import { useChat } from "../context/Chatcontext";
import { useCall } from "../context/CallContext";

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const CHAT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  /* Panel slide-up */
  @keyframes chatPanelUp {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  /* Message pop-in */
  @keyframes msgIn {
    from { transform: scale(.88) translateY(6px); opacity: 0; }
    to   { transform: scale(1)   translateY(0);   opacity: 1; }
  }

  /* Unread badge pulse */
  @keyframes badgePulse {
    0%,100% { transform: scale(1); }
    50%     { transform: scale(1.2); }
  }

  /* Typing dots */
  @keyframes dot { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }

  .chat-fab   { transition: transform .15s, box-shadow .15s; }
  .chat-fab:hover  { transform: scale(1.08); }
  .chat-fab:active { transform: scale(.94); }

  .chat-conv-row   { transition: background .15s; cursor: pointer; }
  .chat-conv-row:hover { background: rgba(255,255,255,.06) !important; }

  .chat-send-btn { transition: background .15s, transform .1s; }
  .chat-send-btn:hover:not(:disabled) { background: #0891b2 !important; transform: scale(1.06); }
  .chat-send-btn:active:not(:disabled) { transform: scale(.94); }

  .chat-input-field {
    flex: 1;
    background: rgba(255,255,255,.06);
    border: 1px solid #21262d;
    border-radius: 12px;
    color: #e6edf3;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    padding: 10px 14px;
    outline: none;
    resize: none;
    transition: border-color .2s;
    line-height: 1.4;
    max-height: 100px;
    overflow-y: auto;
  }
  .chat-input-field::placeholder { color: #7d8590; }
  .chat-input-field:focus { border-color: rgba(45,212,191,.45); }

  .chat-search-field {
    width: 100%;
    box-sizing: border-box;
    background: rgba(255,255,255,.05);
    border: 1px solid #21262d;
    border-radius: 10px;
    color: #e6edf3;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    padding: 8px 12px 8px 34px;
    outline: none;
    transition: border-color .2s;
  }
  .chat-search-field::placeholder { color: #7d8590; }
  .chat-search-field:focus { border-color: rgba(45,212,191,.45); }

  .msg-scroll::-webkit-scrollbar { width: 3px; }
  .msg-scroll::-webkit-scrollbar-track { background: transparent; }
  .msg-scroll::-webkit-scrollbar-thumb { background: #21262d; border-radius: 4px; }

  .conv-scroll::-webkit-scrollbar { width: 3px; }
  .conv-scroll::-webkit-scrollbar-track { background: transparent; }
  .conv-scroll::-webkit-scrollbar-thumb { background: #21262d; border-radius: 4px; }
`;

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const avatarColors = ["#0e7490","#065f46","#7c3aed","#b45309","#be185d","#0369a1","#15803d"];

const getAvatar = (name = "?", size = 34) => {
  const initials = name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase();
  const bg       = avatarColors[name.charCodeAt(0) % avatarColors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: bg, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 700, color: "#fff",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {initials}
    </div>
  );
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDay = (iso) => {
  if (!iso) return "";
  const d   = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

/* ─── Typing indicator ───────────────────────────────────────────────────── */
const TypingDots = () => (
  <div style={{ display: "flex", gap: 3, alignItems: "center", padding: "4px 2px" }}>
    {[0, .2, .4].map((delay, i) => (
      <div key={i} style={{
        width: 5, height: 5, borderRadius: "50%",
        background: "#7d8590",
        animation: `dot .9s ease-in-out ${delay}s infinite`,
      }} />
    ))}
  </div>
);

/* ─── Single message bubble ──────────────────────────────────────────────── */
const Bubble = ({ msg, isMine }) => (
  <div style={{
    display: "flex",
    flexDirection: isMine ? "row-reverse" : "row",
    alignItems: "flex-end",
    gap: 6,
    marginBottom: 6,
    animation: "msgIn .18s ease",
  }}>
    <div style={{
      maxWidth:     "72%",
      background:   isMine
        ? "linear-gradient(135deg, #0891b2, #2dd4bf)"
        : "rgba(255,255,255,.07)",
      color:        isMine ? "#fff" : "#e6edf3",
      borderRadius: isMine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
      padding:      "9px 13px",
      fontSize:     13,
      lineHeight:   1.5,
      wordBreak:    "break-word",
      border:       isMine ? "none" : "1px solid #21262d",
    }}>
      {msg.text}
      <div style={{
        display:    "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap:        4,
        marginTop:  4,
      }}>
        <span style={{ fontSize: 10, opacity: .65, fontFamily: "'DM Mono', monospace" }}>
          {formatTime(msg.timestamp)}
        </span>
        {isMine && (
          <span style={{ fontSize: 11, opacity: msg.read ? 1 : .45 }}>
            {msg.read ? "✓✓" : "✓"}
          </span>
        )}
        {msg._temp && (
          <span style={{ fontSize: 10, opacity: .4 }}>⏳</span>
        )}
      </div>
    </div>
  </div>
);

/* ─── Chat window (open conversation) ───────────────────────────────────── */
const ChatWindow = ({ userId, conv, onBack, sendMessage, currentUserId, onCall }) => {
  const [text, setText]         = useState("");
  const messagesEndRef           = useRef(null);
  const textareaRef              = useRef(null);
  const otherUser                = conv?.user || {};
  const msgs                     = conv?.messages || [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const handleSend = () => {
    if (!text.trim()) return;
    sendMessage(userId, text);
    setText("");
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group messages by day
  const grouped = [];
  let lastDay = null;
  msgs.forEach((m) => {
    const day = formatDay(m.timestamp);
    if (day !== lastDay) { grouped.push({ type: "day", label: day }); lastDay = day; }
    grouped.push({ type: "msg", msg: m });
  });

  return (
    <div style={{
      display:       "flex",
      flexDirection: "column",
      height:        "100%",
    }}>
      {/* Header */}
      <div style={{
        display:      "flex",
        alignItems:   "center",
        gap:          10,
        padding:      "14px 16px",
        borderBottom: "1px solid #21262d",
        flexShrink:   0,
      }}>
        <button onClick={onBack} style={{
          background: "none", border: "none",
          color: "#7d8590", fontSize: 18,
          cursor: "pointer", padding: "0 4px",
          lineHeight: 1,
        }}>‹</button>

        {getAvatar(otherUser.name || "?", 36)}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: "#e6edf3", fontWeight: 600, fontSize: 14,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {otherUser.name || "Unknown"}
          </div>
          <div style={{ color: "#3fb950", fontSize: 11, marginTop: 1 }}>
            ● Online
          </div>
        </div>

        {/* Call button in chat header */}
        {onCall && (
          <button
            onClick={() => onCall(otherUser)}
            title={`Call ${otherUser.name}`}
            style={{
              background:   "rgba(45,212,191,.12)",
              border:       "1px solid rgba(45,212,191,.2)",
              borderRadius: "10px",
              color:        "#2dd4bf",
              width:        34,
              height:       34,
              fontSize:     15,
              cursor:       "pointer",
              display:      "flex",
              alignItems:   "center",
              justifyContent: "center",
              flexShrink:   0,
              transition:   "background .15s",
            }}
          >
            📞
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        className="msg-scroll"
        style={{
          flex:       1,
          overflowY:  "auto",
          padding:    "14px 14px 6px",
          display:    "flex",
          flexDirection: "column",
        }}
      >
        {grouped.length === 0 && (
          <div style={{
            textAlign: "center", color: "#7d8590",
            fontSize: 13, marginTop: "auto", marginBottom: "auto",
            padding: "20px 0",
          }}>
            No messages yet — say hello 👋
          </div>
        )}

        {grouped.map((item, i) =>
          item.type === "day" ? (
            <div key={i} style={{
              textAlign:  "center",
              color:      "#7d8590",
              fontSize:   11,
              margin:     "8px 0",
              fontFamily: "'DM Mono', monospace",
              letterSpacing: ".5px",
            }}>
              {item.label}
            </div>
          ) : (
            <Bubble
              key={item.msg.id}
              msg={item.msg}
              isMine={String(item.msg.senderId) === String(currentUserId)}
            />
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        display:    "flex",
        gap:        8,
        padding:    "10px 14px 14px",
        borderTop:  "1px solid #21262d",
        flexShrink: 0,
        alignItems: "flex-end",
      }}>
        <textarea
          ref={textareaRef}
          className="chat-input-field"
          rows={1}
          placeholder="Type a message… (Enter to send)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className="chat-send-btn"
          onClick={handleSend}
          disabled={!text.trim()}
          style={{
            width:        38,
            height:       38,
            borderRadius: "12px",
            border:       "none",
            background:   text.trim() ? "#2dd4bf" : "rgba(255,255,255,.06)",
            color:        text.trim() ? "#0d1117" : "#7d8590",
            fontSize:     17,
            cursor:       text.trim() ? "pointer" : "not-allowed",
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            flexShrink:   0,
            transition:   "background .2s, color .2s",
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
};

/* ─── Conversation list ──────────────────────────────────────────────────── */
const ConversationList = ({ onlineUsers, conversations, onSelect, search, setSearch }) => {
  // Merge online users with existing conversations
  const allUsers = onlineUsers.filter((u) => u.online);

  // Also include users we've chatted with even if now offline
  const convUserIds = Object.keys(conversations);
  const convOnlyUsers = convUserIds
    .filter((id) => !allUsers.find((u) => String(u.id) === id))
    .map((id) => conversations[id].user)
    .filter(Boolean);

  const combined = [...allUsers, ...convOnlyUsers];
  const filtered = combined.filter((u) =>
    `${u.name} ${u.department || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{
        padding:      "14px 16px 10px",
        borderBottom: "1px solid #21262d",
        flexShrink:   0,
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 10,
        }}>
          <span style={{ color: "#e6edf3", fontWeight: 700, fontSize: 15 }}>
            💬 Messages
          </span>
          <span style={{
            background:   "rgba(45,212,191,.1)",
            color:        "#2dd4bf",
            borderRadius: "99px",
            padding:      "2px 10px",
            fontSize:     11,
            fontWeight:   600,
            border:       "1px solid rgba(45,212,191,.2)",
          }}>
            {allUsers.length} online
          </span>
        </div>

        {/* Search */}
        <div style={{ position: "relative" }}>
          <span style={{
            position: "absolute", left: 10, top: "50%",
            transform: "translateY(-50%)", fontSize: 13,
            color: "#7d8590", pointerEvents: "none",
          }}>🔍</span>
          <input
            className="chat-search-field"
            type="text"
            placeholder="Search staff…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* List */}
      <div
        className="conv-scroll"
        style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}
      >
        {filtered.length === 0 && (
          <div style={{
            color: "#7d8590", fontSize: 13,
            textAlign: "center", padding: "24px 0",
          }}>
            {search ? `No results for "${search}"` : "No staff online"}
          </div>
        )}

        {filtered.map((u) => {
          const conv    = conversations[String(u.id)];
          const unread  = conv?.unread || 0;
          const lastMsg = conv?.messages?.[conv.messages.length - 1];
          const isOnline = allUsers.find((x) => String(x.id) === String(u.id));

          return (
            <div
              key={u.id}
              className="chat-conv-row"
              onClick={() => onSelect(u)}
              style={{
                display:      "flex",
                alignItems:   "center",
                gap:          10,
                padding:      "10px 8px",
                borderRadius: "12px",
                marginBottom: 2,
              }}
            >
              {/* Avatar with online dot */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                {getAvatar(u.name, 40)}
                <span style={{
                  position:     "absolute",
                  bottom:       0, right: 0,
                  width:        10, height: 10,
                  borderRadius: "50%",
                  background:   isOnline ? "#3fb950" : "#7d8590",
                  border:       "2px solid #0d1117",
                }} />
              </div>

              {/* Name + last message */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display:        "flex",
                  justifyContent: "space-between",
                  alignItems:     "baseline",
                  marginBottom:   2,
                }}>
                  <span style={{
                    color: "#e6edf3", fontWeight: 600, fontSize: 13,
                    whiteSpace: "nowrap", overflow: "hidden",
                    textOverflow: "ellipsis", maxWidth: "70%",
                  }}>
                    {u.name}
                  </span>
                  {lastMsg && (
                    <span style={{
                      color: "#7d8590", fontSize: 10,
                      fontFamily: "'DM Mono', monospace", flexShrink: 0,
                    }}>
                      {formatTime(lastMsg.timestamp)}
                    </span>
                  )}
                </div>
                <div style={{
                  color:         unread ? "#e6edf3" : "#7d8590",
                  fontSize:      12,
                  fontWeight:    unread ? 600 : 400,
                  whiteSpace:    "nowrap",
                  overflow:      "hidden",
                  textOverflow:  "ellipsis",
                }}>
                  {lastMsg
                    ? (lastMsg.senderId !== String(u.id) ? "You: " : "") + lastMsg.text
                    : u.department || "Start a conversation"}
                </div>
              </div>

              {/* Unread badge */}
              {unread > 0 && (
                <div style={{
                  background:   "#2dd4bf",
                  color:        "#0d1117",
                  borderRadius: "99px",
                  minWidth:     18,
                  height:       18,
                  display:      "flex",
                  alignItems:   "center",
                  justifyContent: "center",
                  fontSize:     10,
                  fontWeight:   700,
                  padding:      "0 5px",
                  flexShrink:   0,
                  animation:    "badgePulse 1.5s ease-in-out infinite",
                }}>
                  {unread > 99 ? "99+" : unread}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Main floating chat widget ──────────────────────────────────────────── */
const FloatingChatWidget = () => {
  const {
    conversations, activeChat, isChatOpen,
    totalUnread, openChat, sendMessage,
    closeChat, setActiveChat, currentUserId,
  } = useChat();

  const { onlineUsers, startCall } = useCall();

  const [panelOpen, setPanelOpen] = useState(false);
  const [search, setSearch]       = useState("");

  const togglePanel = () => {
    setPanelOpen((p) => !p);
    if (panelOpen) setSearch("");
  };

  const handleSelectUser = (targetUser) => {
    openChat(targetUser);
  };

  const handleBack = () => {
    setActiveChat(null);
  };

  const activeConv = activeChat ? conversations[activeChat] : null;

  return (
    <>
      <style>{CHAT_STYLES}</style>

      <div style={{
        position: "fixed",
        right:    90,   // sits to the left of the call widget FAB
        bottom:   20,
        zIndex:   9998,
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Chat panel */}
        {panelOpen && (
          <div style={{
            width:         "340px",
            height:        "500px",
            background:    "linear-gradient(160deg, #161b22, #0d1117)",
            borderRadius:  "20px",
            border:        "1px solid #21262d",
            marginBottom:  "12px",
            animation:     "chatPanelUp .22s ease",
            display:       "flex",
            flexDirection: "column",
            overflow:      "hidden",
            boxShadow:     "0 16px 48px rgba(0,0,0,.45)",
          }}>
            {activeChat && activeConv ? (
              <ChatWindow
                userId={activeChat}
                conv={activeConv}
                onBack={handleBack}
                sendMessage={sendMessage}
                currentUserId={currentUserId}
                onCall={startCall}
              />
            ) : (
              <ConversationList
                onlineUsers={onlineUsers}
                conversations={conversations}
                onSelect={handleSelectUser}
                search={search}
                setSearch={setSearch}
              />
            )}
          </div>
        )}

        {/* FAB */}
        <div style={{ position: "relative", display: "inline-block" }}>
          <button
            className="chat-fab"
            onClick={togglePanel}
            title="Staff chat"
            style={{
              width:        58,
              height:       58,
              borderRadius: "50%",
              border:       "none",
              background:   panelOpen
                ? "linear-gradient(135deg, #2dd4bf, #0891b2)"
                : "linear-gradient(135deg, #1e3a5f, #1f6feb)",
              color:        "#fff",
              fontSize:     22,
              cursor:       "pointer",
              boxShadow:    "0 8px 24px rgba(31,111,235,.35)",
              display:      "flex",
              alignItems:   "center",
              justifyContent: "center",
            }}
          >
            {panelOpen ? "✕" : "💬"}
          </button>

          {/* Unread badge */}
          {totalUnread > 0 && !panelOpen && (
            <div style={{
              position:     "absolute",
              top:          -3, right: -3,
              minWidth:     18, height: 18,
              borderRadius: "99px",
              background:   "#f87171",
              border:       "2px solid #0d1117",
              display:      "flex",
              alignItems:   "center",
              justifyContent: "center",
              fontSize:     9,
              fontWeight:   700,
              color:        "#fff",
              padding:      "0 4px",
              zIndex:       2,
              animation:    "badgePulse 1.5s ease-in-out infinite",
            }}>
              {totalUnread > 99 ? "99+" : totalUnread}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FloatingChatWidget;